import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { BellRing } from "lucide-react";
import { isPushSupported, getPushSubscription, subscribeToPush, unsubscribeFromPush } from "@/utils/push";

export const PushNotificationSettings = () => {
  const [supported] = useState(isPushSupported());
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    getPushSubscription().then((sub) => setEnabled(!!sub)).catch(() => {});
  }, [supported]);

  const handleToggle = async (checked) => {
    setBusy(true);
    try {
      if (checked) {
        await subscribeToPush();
        setEnabled(true);
        toast.success("Push-Benachrichtigungen aktiviert!");
      } else {
        await unsubscribeFromPush();
        setEnabled(false);
        toast.success("Push-Benachrichtigungen deaktiviert");
      }
    } catch (error) {
      if (error.message === "permission_denied") {
        toast.error("Benachrichtigungen wurden im Browser blockiert. Bitte in den Browser-Einstellungen erlauben.");
      } else {
        toast.error("Push-Benachrichtigungen konnten nicht aktiviert werden");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-card border-border" data-testid="push-settings-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <BellRing className="w-5 h-5 text-primary" />
          Push-Benachrichtigungen
        </CardTitle>
      </CardHeader>
      <CardContent>
        {supported ? (
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="font-medium">Benachrichtigungen auf diesem Gerät</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Erhalte sofort Nachrichten von Restaurants, neue Abzeichen und Treue-Belohnungen –
                auch wenn die App geschlossen ist.
              </p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={handleToggle}
              disabled={busy}
              data-testid="push-toggle"
            />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Dein Browser unterstützt keine Push-Benachrichtigungen.
            Auf dem iPhone: Füge Étoile zuerst zum Home-Bildschirm hinzu (Teilen → „Zum Home-Bildschirm").
          </p>
        )}
      </CardContent>
    </Card>
  );
};
